/** @format */

import { HoneycombWebSDK, type HoneycombOptions } from '@honeycombio/opentelemetry-web';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';

const COOKIE_NAME = 'swimlane_as_exp_dsg';

/**
 * Known synthetic/bot user-agent patterns.
 * Covers common monitoring services and headless browser runtimes.
 */
const SYNTHETIC_UA_PATTERNS = [
  /PINGDOM/i,
  /DatadogSynthetics/i,
  /NewRelicPinger/i,
  /HeadlessChrome/i,
  /Chrome-Lighthouse/i,
  // Match "bot" as a standalone word or as a "<Name>bot/<version>" UA token (Googlebot/2.1, bingbot/2.0),
  // but not as a substring of unrelated words (e.g. the Cubot phone brand).
  /\bbot\b|bot\//i,
  /crawler|spider/i,
];

/**
 * Determines whether the current request originates from a synthetic monitor
 * or headless browser rather than a real user.
 *
 * Derived once at SDK initialization so the cost is paid only once per page
 * load (negligible — no DOM access, no async work).
 *
 * Returns `false` in SSR / non-browser contexts where `navigator` is absent.
 *
 * @returns `true` if the client appears to be a bot or synthetic monitor.
 */
export function isSynthetic(): boolean {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent || '';

  if (SYNTHETIC_UA_PATTERNS.some((pattern) => pattern.test(userAgent))) return true;

  return hasHeadlessBrand();
}

/**
 * User-Agent Client Hints (`navigator.userAgentData`) are Chromium-only and missing from the DOM typings,
 * so the value is read as `unknown` and narrowed instead of cast.
 */
function hasHeadlessBrand(): boolean {
  const userAgentData: unknown = Reflect.get(navigator, 'userAgentData');
  if (typeof userAgentData !== 'object' || userAgentData === null) return false;
  if (!('brands' in userAgentData) || !Array.isArray(userAgentData.brands)) return false;

  const brands: unknown[] = userAgentData.brands;
  return brands.some(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      'brand' in entry &&
      typeof entry.brand === 'string' &&
      entry.brand.toLowerCase().includes('headless'),
  );
}

function getSwimlaneAsExpDsgCookie(): number | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`));
  if (!match) return undefined;
  const value = decodeURIComponent(match[1]);
  const num = Number(value);
  return Number.isNaN(num) ? undefined : num;
}

function getBusinessContext() {
  if (globalThis.window === undefined) return {};

  const pathname = globalThis.window.location.pathname;
  const search = globalThis.window.location.search;
  const context: Record<string, string> = {};

  // Extract product SKU from PDP URLs (e.g., /p/product-name/SKU123)
  if (pathname.includes('/p/')) {
    const parts = pathname.split('/');
    const sku = parts.at(-1);
    if (sku) context['product.sku'] = sku;
  }

  // Extract search query from URL
  if (search.includes('searchTerm=')) {
    const regex = /searchTerm=([^&]*)/;
    const match = regex.exec(search);
    if (match) context['search.query'] = decodeURIComponent(match[1]);
  }

  // Check if user is authenticated (simple cookie check, no heavy logic)
  if (document.cookie.includes('WC_AUTHENTICATION_')) {
    context['user.authenticated'] = 'true';
  }

  // Track if there were recent JS errors (non-blocking check)
  const errorCount = sessionStorage.getItem('otel_error_count');
  if (errorCount && Number.parseInt(errorCount, 10) > 0) {
    context['has.recent.errors'] = 'true';
    context['error.count'] = errorCount;
  }

  return context;
}

const configDefaults = {
  ignoreNetworkEvents: true,
  propagateTraceHeaderCorsUrls: [
    // Regex to match your backend URLs. Update to the domains you wish to include.
    // eslint-disable-next-line no-useless-escape
    /https:\/\/(?!(aem\.))[^\/]*\.(dickssportinggoods|publiclands|golfgalaxy|goinggoinggone|dksxchange)\.com/,
  ],
};

// SpanProcessor functionality will be moved to utag, leave it for now as a reference
// let contextSpanProcessor: CustomSpanProcessor | null = null;

export function startHoneycomb(
  apiKey = '',
  serviceName = '',
  appName = '',
  brand = '',
  // spanProcessor?: InstanceType<typeof CustomSpanProcessor> Custom SpanP rocessor functionality will be moved to Utag, leave it for now as a reference
): HoneycombWebSDK {
  const swimlaneCookie = getSwimlaneAsExpDsgCookie();
  const businessContext = getBusinessContext();

  const config: HoneycombOptions = {
    endpoint: 'https://dcsg.us1.raas-honeycomb.io/v1/traces', // Send to DSG specific instance of Honeycomb. If not defined, defaults to sending to US instance.
    debug: true, // Set to false for production environment.
    apiKey: apiKey, // Replace with your Honeycomb Ingest API Key.
    serviceName: serviceName, // Replace with your application name. Honeycomb uses this string to find your dataset when we receive your data. When no matching dataset exists, we create a new one with this name if your API Key has the appropriate permissions.
    sampleRate: 20, // 2026-06-01 At the request of RE, sample 1 out of every 20 to reduce load on Honeycomb trace volume
    resourceAttributes: {
      'app.name': appName || 'unknown',
      'brand.name': brand || 'unknown',
      'swimlane.id': swimlaneCookie || 'unknown',
      ...businessContext,
      'client.is_synthetic': isSynthetic(), // keep last so business context can never override it
    },
    spanProcessors: [],
    instrumentations: [
      getWebAutoInstrumentations({
        // Loads custom configuration for xml-http-request instrumentation.
        '@opentelemetry/instrumentation-xml-http-request': configDefaults,
        '@opentelemetry/instrumentation-fetch': configDefaults,
        '@opentelemetry/instrumentation-document-load': configDefaults,
      }),
    ],
    webVitalsInstrumentationConfig: {
      vitalsToTrack: ['CLS', 'FCP', 'INP', 'LCP', 'TTFB'],
      cls: {
        reportAllChanges: true,
      },
      fcp: {
        reportAllChanges: true,
      },
      inp: {
        reportAllChanges: true,
      },
      lcp: {
        reportAllChanges: true,
      },
      ttfb: {
        reportAllChanges: true,
      },
    },
  };
  // Custom SpanProcessor functionality will be moved to Utag, leave it for now as a reference
  // if (spanProcessor) {
  //  contextSpanProcessor = spanProcessor;
  //  config.spanProcessors.push(contextSpanProcessor);
  // }
  const sdk = new HoneycombWebSDK(config);
  sdk.start();

  // Lightweight error tracking (non-blocking, no performance impact)
  if (globalThis.window !== undefined) {
    globalThis.window.addEventListener(
      'error',
      () => {
        try {
          const current = Number.parseInt(sessionStorage.getItem('otel_error_count') || '0', 10);
          sessionStorage.setItem('otel_error_count', String(current + 1));
        } catch (e) {
          // Silently fail if sessionStorage is unavailable - this is expected in SSR or restricted contexts
          // eslint-disable-next-line no-console
          console.debug('OpenTelemetry: Unable to track error count', e);
        }
      },
      { passive: true },
    );
  }

  return sdk;
}
// Custom Span Processor functionality will be moved to Utag, leave it for now as a reference
// Use this function to set the spanProcessor information later.
// export function registerspanProcessor(provider: Record<string, string> | null): void {
//  if (contextSpanProcessor) {
//      contextSpanProcessor.setProvider(provider);
//  } else {
//      throw new Error('HoneyComb: Context Span Processor does not exists');
//  }
// }
